import type { AppLanguage } from '../i18n/types';

export const TIME_HOURS_24 = Array.from({ length: 24 }, (_, h) => String(h).padStart(2, '0'));
export const TIME_MINUTES_60 = Array.from({ length: 60 }, (_, m) => String(m).padStart(2, '0'));

/** Normalize wire/UI time to `HH:MM` for `<input type="time">` and 24h selects. */
export function normalizeTimeHm(raw: string): string {
  if (raw == null) {
    return '';
  }
  const t = String(raw).trim();
  if (t === '') {
    return '';
  }
  if (/^\d{2}:\d{2}$/.test(t)) {
    return t;
  }
  if (/^\d{2}:\d{2}:\d{2}/.test(t)) {
    return t.slice(0, 5);
  }
  const m = t.match(/^(\d{1,2}):(\d{1,2})$/);
  if (m) {
    const h = Math.min(23, Math.max(0, parseInt(m[1], 10)));
    const min = Math.min(59, Math.max(0, parseInt(m[2], 10)));
    return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
  }
  return '';
}

export function parseTimeHmParts(value: string): { hour: string; minute: string } {
  const normalized = normalizeTimeHm(value);
  if (!normalized) {
    return { hour: '00', minute: '00' };
  }
  const [hour, minute] = normalized.split(':');
  return { hour, minute };
}

export function joinTimeHmParts(hour: string, minute: string): string {
  const h = Math.min(23, Math.max(0, parseInt(hour, 10) || 0));
  const m = Math.min(59, Math.max(0, parseInt(minute, 10) || 0));
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Hour segment (`HH`) from a wire/UI time value. */
export function hourFromTimeHm(value: string): string {
  return parseTimeHmParts(normalizeTimeHm(value)).hour;
}

/** Normalize `HH` or `HH:MM` option lists to two-digit hours for `<select>`. */
export function normalizeHourOptionList(options: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const opt of options) {
    const hour = /^\d{2}:/.test(opt) ? opt.slice(0, 2) : String(opt).padStart(2, '0');
    if (!seen.has(hour)) {
      seen.add(hour);
      out.push(hour);
    }
  }
  return out;
}

/** Booking pickers: whole hours only, displayed as `HH:00` (minutes, never seconds). */
export function formatRoundHourTimeLabel(hour: string): string {
  const padded = String(hour).padStart(2, '0');
  return `${padded}:00`;
}

export function uses24HourTimePicker(language: AppLanguage): boolean {
  return language === 'he';
}

/** @deprecated Native `type="time"` ignores locale on many Windows setups; use `LocaleTimeInput`. */
export function htmlLangForTimeInput(language: AppLanguage): string | undefined {
  return language === 'he' ? 'he-IL' : undefined;
}
